import type { BillingV1FeatureEntityUsage } from '@cast/types';

import { CHART_COLORS } from './constants';

// ─── Types ───────────────────────────────────────────────────────────────────

export type StackedBarDataPoint = {
  /** ISO date string (YYYY-MM-DD). Also used as the timestamp field for ChartTooltipTimestampHeader. */
  day: string;
  /** Same value as `day` — required by ChartTooltipTimestampHeader which reads `data.timestamp`. */
  timestamp: string;
  chartTimestampUnix: number;
  [entityId: string]: number | string;
};

// ─── Color ───────────────────────────────────────────────────────────────────

/**
 * Deterministic color index for an entity.
 * Uses a simple hash of the entityId string modulo palette length so the same
 * entity always gets the same color regardless of order.
 */
export const getEntityColorIndex = (entityId: string): number => {
  let hash = 0;
  for (let i = 0; i < entityId.length; i++) {
    hash = (hash * 31 + entityId.charCodeAt(i)) >>> 0;
  }
  return hash % CHART_COLORS.length;
};

/**
 * Returns the palette token string for a given entity key.
 * Token must be resolved to a hex color via useTheme() before use in CSS.
 */
export const getEntityColorToken = (entityId: string): string => {
  return CHART_COLORS[getEntityColorIndex(entityId)];
};

// ─── Chart data ──────────────────────────────────────────────────────────────

import dayjs from 'dayjs';

/**
 * Transforms the flat entities+dailyUsages API response into the format
 * required by the recharts BarStack:
 *   [{ day, chartTimestampUnix, [entityId]: value, … }, …]
 *
 * Fills every day in [periodFrom, periodTo] so the X-axis ticks are
 * contiguous even when some days have no data.
 */
export const buildStackedBarChartData = (
  entities: BillingV1FeatureEntityUsage[],
  periodFrom: string,
  periodTo: string
): StackedBarDataPoint[] => {
  const start = dayjs(periodFrom);
  const end = dayjs(periodTo);

  // Build a sorted list of all days in range
  const allDays: string[] = [];
  let cursor = start;
  while (!cursor.isAfter(end)) {
    allDays.push(cursor.format('YYYY-MM-DD'));
    cursor = cursor.add(1, 'day');
  }

  // Accumulate per-entity per-day values
  const dayEntityMap = new Map<string, Record<string, number>>();
  for (const day of allDays) {
    dayEntityMap.set(day, {});
  }

  for (const entity of entities) {
    const entityKey = entity.entityId ?? entity.entityName ?? '';
    if (!entityKey) continue;
    for (const daily of entity.dailyUsages ?? []) {
      if (!daily.day) continue;
      const entry = dayEntityMap.get(daily.day);
      if (!entry) continue; // outside range
      entry[entityKey] = (entry[entityKey] ?? 0) + (daily.value ?? 0);
    }
  }

  return allDays.map((day) => ({
    day,
    timestamp: day,
    chartTimestampUnix: dayjs(day).unix(),
    ...(dayEntityMap.get(day) ?? {}),
  }));
};
